import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import { MaterialReactTable, useMaterialReactTable } from 'material-react-table';
import { MRT_Localization_FR } from 'material-react-table/locales/fr';
import axios from '../../../api/axiosInstance';

const emptyConfig = {
  enabled: false,
  apiUrl: '',
  apiKey: '',
  clientVersion: '3.6.1',
  authMode: 'bearer',
  resourcePaths: [
    'about',
    'companies',
    'provider/companies',
    'backupServers',
    'protectedWorkloads/computers',
    'protectedWorkloads/vms',
    'protectedWorkloads/fileShares',
    'protectedWorkloads/objectStorage',
    'protectedWorkloads/microsoft365Objects',
    'protectedWorkloads/computersManagedByConsole/jobs',
    'infrastructure/backupAgents/jobs',
    'infrastructure/backupAgents/windows/jobs',
    'infrastructure/backupAgents/linux/jobs',
    'infrastructure/backupAgents/mac/jobs',
    'infrastructure/backupServers/jobs',
    'infrastructure/backupServers/jobs/agentJobs',
    'infrastructure/backupServers/jobs/backupVmJobs',
    'infrastructure/backupServers/jobs/replicationVmJobs',
    'infrastructure/backupServers/jobs/cdpReplicationJobs',
    'infrastructure/backupServers/jobs/backupCopyJobs',
    'infrastructure/backupServers/jobs/simpleBackupCopyJobs',
    'infrastructure/backupServers/jobs/fileShareJobs',
    'infrastructure/backupServers/jobs/fileShareCopyJobs',
    'infrastructure/backupServers/jobs/objectStorageBackupJobs',
    'infrastructure/backupServers/jobs/objectStorageBackupCopyJobs',
    'infrastructure/backupServers/jobs/vmCopyJobs',
    'infrastructure/backupServers/jobs/fileCopyJobs',
    'infrastructure/backupServers/jobs/fileTapeJobs',
    'infrastructure/backupServers/jobs/backupTapeJobs',
    'infrastructure/vb365Servers/organizations/jobs',
    'infrastructure/vb365Servers/organizations/jobs/backup',
    'infrastructure/vb365Servers/organizations/jobs/copy',
    'discovery/rules',
    'licensing/usage/organizations',
    'licensing/reports/latest',
    'licensing/console',
  ],
};

function formatDate(value) {
  if (!value) return 'Jamais';
  return new Date(value).toLocaleString('fr-FR', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function getOrgLabel(org) {
  if (!org) return '';
  return org.parent_name ? `${org.name} (${org.parent_name})` : org.name;
}

export default function VcspSettingsPage() {
  const [config, setConfig] = useState(emptyConfig);
  const [mappings, setMappings] = useState([]);
  const [organizations, setOrganizations] = useState([]);
  const [resources, setResources] = useState([]);
  const [resourceMappings, setResourceMappings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const loadConfig = async () => {
    const { data } = await axios.get('/api/settings/vcsp/config');
    setConfig((current) => ({
      ...emptyConfig,
      ...data,
      apiKey: current.apiKey || '',
      resourcePaths: data.resourcePaths || emptyConfig.resourcePaths,
    }));
  };

  const loadResources = async () => {
    const { data } = await axios.get('/api/settings/vcsp/resources');
    setOrganizations(data.organizations || []);
    setResources(data.resources || []);
    setResourceMappings((data.resources || []).filter((resource) => (resource.resourceKind === 'repo' || resource.resourceKind === 'job') && !String(resource.resourceType || '').endsWith('.usage')));
    setMappings((data.mappings || []).map((mapping) => ({
      ...mapping,
      organization: mapping.organization || null,
      enabled: mapping.enabled !== false,
      suggested: Boolean(mapping.suggestedOrganization && !mapping.organization),
    })));
  };

  const hydrate = useCallback(async () => {
    setLoading(true);
    try {
      await Promise.all([loadConfig(), loadResources()]);
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Impossible de charger VCSP.' });
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    hydrate();
  }, [hydrate]);

  const saveConfig = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put('/api/settings/vcsp/config', {
        enabled: config.enabled,
        apiUrl: config.apiUrl,
        apiKey: config.apiKey,
        clientVersion: config.clientVersion,
        authMode: config.authMode,
        resourcePaths: config.resourcePaths,
      });
      setConfig((current) => ({ ...current, ...data, apiKey: '' }));
      setFeedback({ type: 'success', message: 'Configuration VCSP sauvegardée.' });
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || error.response?.data?.error || 'Sauvegarde VCSP impossible.' });
    } finally {
      setSaving(false);
    }
  };

  const testConfig = async () => {
    setTesting(true);
    try {
      const { data } = await axios.post('/api/settings/vcsp/test');
      setConfig((current) => ({ ...current, ...data, apiKey: '' }));
      setFeedback({ type: 'success', message: 'Connexion VCSP OK.' });
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.error || error.response?.data?.message || 'Connexion VCSP impossible.' });
    } finally {
      setTesting(false);
    }
  };

  const syncResources = async () => {
    setSyncing(true);
    try {
      const { data } = await axios.post('/api/settings/vcsp/resources/sync');
      await loadResources();
      const errors = data.summary?.errors?.length || 0;
      setFeedback({
        type: errors ? 'warning' : 'success',
        message: `Synchronisation VCSP terminée : ${data.summary?.resources || 0} ressource(s), ${errors} erreur(s).`,
      });
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.error || error.response?.data?.message || 'Synchronisation VCSP impossible.' });
    } finally {
      setSyncing(false);
    }
  };

  const saveMappings = async () => {
    setSaving(true);
    try {
      await axios.put('/api/settings/vcsp/mappings', {
        mappings: mappings.map((mapping) => ({
          vcspCompanyUid: mapping.vcspCompanyUid,
          vcspCompanyName: mapping.vcspCompanyName,
          organization: mapping.organization?._id || mapping.organization || null,
          enabled: mapping.enabled !== false,
          notes: mapping.notes || '',
        })),
      });
      await loadResources();
      setFeedback({ type: 'success', message: 'Mapping VCSP sauvegardé.' });
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Sauvegarde du mapping impossible.' });
    } finally {
      setSaving(false);
    }
  };

  const updateResourceMapping = (id, organization) => {
    setResourceMappings((current) => current.map((resource) => (
      resource._id === id ? { ...resource, Organization: organization } : resource
    )));
  };

  const saveResourceMappings = async () => {
    setSaving(true);
    try {
      await axios.put('/api/settings/vcsp/resource-mappings', {
        mappings: resourceMappings.map((resource) => ({
          resourceId: resource._id,
          organizationId: resource.Organization?._id || null,
        })),
      });
      await loadResources();
      setFeedback({ type: 'success', message: 'Mapping des repos et jobs sauvegardé.' });
    } catch (error) {
      setFeedback({ type: 'error', message: error.response?.data?.message || 'Sauvegarde du mapping des ressources impossible.' });
    } finally {
      setSaving(false);
    }
  };

  const updateMapping = (uid, patch) => {
    setMappings((current) => current.map((mapping) => (
      mapping.vcspCompanyUid === uid ? { ...mapping, ...patch, suggested: false } : mapping
    )));
  };

  const columns = useMemo(() => [
    { accessorKey: 'vcspCompanyName', header: 'Société VCSP', size: 220, filterVariant: 'autocomplete' },
    {
      accessorKey: 'organization.name',
      header: 'Client Omnibox',
      size: 280,
      Cell: ({ row }) => (
        <Autocomplete
          size="small"
          options={organizations}
          value={row.original.organization || null}
          getOptionLabel={getOrgLabel}
          isOptionEqualToValue={(option, value) => option._id === value?._id}
          onChange={(_, value) => updateMapping(row.original.vcspCompanyUid, { organization: value })}
          renderInput={(params) => <TextField {...params} placeholder="Non mappé" />}
        />
      ),
    },
    {
      accessorKey: 'suggestedOrganization.name',
      header: 'Suggestion',
      size: 220,
      Cell: ({ row }) => row.original.suggestedOrganization ? (
        <Button
          size="small"
          onClick={() => updateMapping(row.original.vcspCompanyUid, { organization: row.original.suggestedOrganization })}
        >
          {getOrgLabel(row.original.suggestedOrganization)}
        </Button>
      ) : '-',
    },
    {
      accessorKey: 'enabled',
      header: 'Actif',
      size: 90,
      Cell: ({ row }) => (
        <Switch
          size="small"
          checked={row.original.enabled !== false}
          onChange={(event) => updateMapping(row.original.vcspCompanyUid, { enabled: event.target.checked })}
        />
      ),
    },
    {
      accessorKey: 'lastSeenAt',
      header: 'Dernière vue',
      size: 160,
      Cell: ({ cell }) => formatDate(cell.getValue()),
    },
    {
      accessorKey: 'notes',
      header: 'Note',
      size: 220,
      Cell: ({ row }) => (
        <TextField
          size="small"
          value={row.original.notes || ''}
          onChange={(event) => updateMapping(row.original.vcspCompanyUid, { notes: event.target.value })}
          fullWidth
        />
      ),
    },
  ], [organizations]);

  const resourceMappingColumns = useMemo(() => [
    { accessorKey: 'resourceKind', header: 'Type', size: 100, Cell: ({ cell }) => <Chip size="small" label={cell.getValue() === 'repo' ? 'Repo' : 'Job'} color={cell.getValue() === 'repo' ? 'success' : 'primary'} /> },
    { accessorKey: 'name', header: 'Repo / Job', size: 280 },
    { accessorKey: 'companyName', header: 'Société VCSP', size: 200 },
    {
      id: 'organization',
      header: 'Client Omnibox',
      size: 300,
      accessorFn: (row) => getOrgLabel(row.Organization),
      Cell: ({ row }) => (
        <Autocomplete
          size="small"
          options={organizations}
          value={row.original.Organization || null}
          getOptionLabel={getOrgLabel}
          isOptionEqualToValue={(option, value) => option._id === value?._id}
          onChange={(_, value) => updateResourceMapping(row.original._id, value)}
          renderInput={(params) => <TextField {...params} placeholder="Non mappé" />}
        />
      ),
    },
    { accessorKey: 'lastSeenAt', header: 'Dernière vue', size: 170, Cell: ({ cell }) => formatDate(cell.getValue()) },
  ], [organizations]);
  
  const resourceTable = useMaterialReactTable({
    columns: resourceMappingColumns,
    data: resourceMappings,
    localization: MRT_Localization_FR,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    initialState: { density: 'compact', showColumnFilters: true, showGlobalFilter: true, pagination: { pageSize: 10, pageIndex: 0 } },
    state: { isLoading: loading || syncing },
  });

  const table = useMaterialReactTable({
    columns,
    data: mappings,
    localization: MRT_Localization_FR,
    enableColumnFilters: true,
    enableGlobalFilter: true,
    initialState: {
      density: 'compact',
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageSize: 10, pageIndex: 0 },
    },
    state: { isLoading: loading || syncing },
  });

  const resourceSummary = useMemo(() => (
    resources.reduce((acc, resource) => {
      acc[resource.resourceType] = (acc[resource.resourceType] || 0) + 1;
      return acc;
    }, {})
  ), [resources]);

  if (loading) {
    return (
      <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Paper>
    );
  }

  return (
    <Stack spacing={2}>
      {feedback && (
        <Alert severity={feedback.type} onClose={() => setFeedback(null)}>
          {feedback.message}
        </Alert>
      )}

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }}>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>VCSP</Typography>
              <Typography variant="body2" color="text.secondary">API Veeam Service Provider Console et mapping clients.</Typography>
            </Box>
            <Chip color={config.enabled ? 'success' : 'default'} label={config.enabled ? 'Activé' : 'Désactivé'} />
          </Stack>

          <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5}>
            <TextField
              label="URL API"
              value={config.apiUrl || ''}
              onChange={(event) => setConfig((current) => ({ ...current, apiUrl: event.target.value }))}
              placeholder="https://vcsp.exemple.tld/api/v3"
              sx={{ flex: 2 }}
            />
            <TextField
              label={config.hasApiKey ? `API key (${config.apiKeyMasked})` : 'API key'}
              type="password"
              value={config.apiKey || ''}
              onChange={(event) => setConfig((current) => ({ ...current, apiKey: event.target.value }))}
              sx={{ flex: 1.5 }}
            />
            <TextField
              label="Version API"
              value={config.clientVersion || ''}
              onChange={(event) => setConfig((current) => ({ ...current, clientVersion: event.target.value }))}
              sx={{ width: 150 }}
            />
            <FormControl sx={{ minWidth: 160 }}>
              <InputLabel>Auth</InputLabel>
              <Select
                label="Auth"
                value={config.authMode || 'bearer'}
                onChange={(event) => setConfig((current) => ({ ...current, authMode: event.target.value }))}
              >
                <MenuItem value="bearer">Bearer</MenuItem>
                <MenuItem value="api-key">Authorization ApiKey</MenuItem>
                <MenuItem value="x-api-key">X-API-Key</MenuItem>
                <MenuItem value="veeam-api-key">X-Veeam-API-Key</MenuItem>
              </Select>
            </FormControl>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography variant="body2">Actif</Typography>
              <Switch checked={Boolean(config.enabled)} onChange={(event) => setConfig((current) => ({ ...current, enabled: event.target.checked }))} />
            </Stack>
          </Stack>

          <TextField
            label="Ressources synchronisées"
            multiline
            minRows={3}
            value={(config.resourcePaths || []).join('\n')}
            onChange={(event) => setConfig((current) => ({ ...current, resourcePaths: event.target.value.split('\n').map((line) => line.trim()).filter(Boolean) }))}
          />

          <Stack direction="row" spacing={1} flexWrap="wrap">
            <Button variant="contained" onClick={saveConfig} disabled={saving}>Sauvegarder</Button>
            <Button variant="outlined" onClick={testConfig} disabled={testing || saving}>{testing ? 'Test...' : 'Tester'}</Button>
            <Button variant="outlined" onClick={syncResources} disabled={syncing || saving}>{syncing ? 'Synchronisation...' : 'Synchroniser'}</Button>
          </Stack>

          <Typography variant="caption" color="text.secondary">
            Dernier test : {formatDate(config.lastTestAt)} {config.lastTestMessage ? `- ${config.lastTestMessage}` : ''}
          </Typography>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2 }}>
        <Stack spacing={1.5}>
          <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
            <Typography variant="h6" sx={{ fontWeight: 800, mr: 1 }}>Ressources</Typography>
            {Object.entries(resourceSummary).map(([type, count]) => (
              <Chip key={type} label={`${type} ${count}`} size="small" />
            ))}
          </Stack>
          <Typography variant="body2" color="text.secondary">{resources.length} ressource(s) synchronisée(s).</Typography>
        </Stack>
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 800 }}>Mapping repos et jobs</Typography>
            <Typography variant="body2" color="text.secondary">Associez chaque dépôt ou job VCSP à une organisation Omnibox.</Typography>
          </Box>
          <Button variant="contained" onClick={saveResourceMappings} disabled={saving || syncing}>Sauvegarder le mapping</Button>
        </Stack>
        <MaterialReactTable table={resourceTable} />
      </Paper>

      <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
          <Typography variant="h6" sx={{ fontWeight: 800 }}>Mapping sociétés</Typography>
          <Button variant="contained" onClick={saveMappings} disabled={saving || syncing}>Sauvegarder le mapping</Button>
        </Stack>
        <MaterialReactTable table={table} />
      </Paper>
    </Stack>
  );
}
